import React from 'react';
import { Link } from 'react-router-dom';
import { Checkbox } from '@this/shared/ui/inputs/checkbox';
import { lighten } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import moment from 'moment';
import type { ReceiptRecord } from '@this/src/types/receipt_record';
import { createTheme, type Themes } from '@this/constants/themes';
import type { AITTheme } from '@this/shared/ui/theme';

interface TripReceiptRowProps {
  receiptRecord: ReceiptRecord;
  selectedTrips: string[];
  inProgressTripIds: string[];
  onSelectTrip: (tripId: string) => void;
  isDisabled: boolean;
}

const CustomTooltip = withStyles({
  tooltip: {
    backgroundColor: 'rgba(97, 97, 97, 0.9)',
    color: 'white',
    fontSize: '12px',
    padding: '8px 12px',
    borderRadius: '4px',
    pointerEvents: 'auto'
  },
  popper: {
    pointerEvents: 'auto'
  }
})(Tooltip);

export const TripReceiptRow: React.FC<TripReceiptRowProps> = React.memo(
  ({ receiptRecord, selectedTrips, inProgressTripIds, onSelectTrip, isDisabled }) => {
    const theme = createTheme(1);
    const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

    React.useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth <= 768);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    const formatDate = (dateString: string | null) => {
      if (!dateString) return '';
      return moment(dateString, moment.ISO_8601).format('YYYY/MM/DD');
    };

    const formatPrice = (price: number) => {
      return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const getDateRange = () => {
      if (receiptRecord.cache_start_time && receiptRecord.cache_end_time) {
        const startDate = formatDate(receiptRecord.cache_start_time.toString());
        const endDate = formatDate(receiptRecord.cache_end_time.toString());
        return `${startDate}～${endDate}`;
      }
      return '';
    };

    const dateRange = getDateRange();
    const originText = receiptRecord.departure || '';
    const destinationText = receiptRecord.destination || '';
    const totalPrice = receiptRecord.total_price || 0;

    const hasDownloadInfo =
      (typeof receiptRecord.downloaded_count === 'number' && receiptRecord.downloaded_count > 0) ||
      (receiptRecord.downloaded_at && receiptRecord.downloaded_at !== null);

    const getStatusDisplay = () => {
      if (receiptRecord.receipt_revision_status === 'revising') {
        return '破棄訂正中';
      }
      if (!receiptRecord.can_issue_receipt) {
        return '発行待ち';
      }
      return null;
    };

    const statusText = getStatusDisplay();
    const styles = getStyles(receiptRecord.receipt_revision_status, theme, isMobile);

    return (
      <div style={styles.simpleCardWrap}>
        <div style={styles.simpleCard} key={`${receiptRecord.id}`}>
          <div style={styles.cardFlex}>
            <div style={styles.cardFlexLeftCheck}>
              {receiptRecord.can_issue_receipt ? (
                <Checkbox
                  checked={selectedTrips.includes(`${receiptRecord.id}`)}
                  onChange={() => onSelectTrip(`${receiptRecord.id}`)}
                  disabled={isDisabled}
                />
              ) : (
                <div style={{ width: '20px' }} />
              )}
            </div>
            <div style={styles.cardFlexLeft}>
              <div style={styles.contentFlex}>
                <div style={styles.contentFlexLeft}>
                  <div style={styles.contentRow}>
                    <h3 style={styles.contentTitle}>
                      <span data-wovn-ignore>{dateRange}</span>
                    </h3>
                  </div>
                  <div style={styles.contentRow}>
                    <p style={styles.contentId}>
                      旅程番号：<span data-wovn-ignore>{receiptRecord.id}</span>
                    </p>
                  </div>
                  <div style={styles.contentRow}>
                    <p style={styles.contentText}>
                      出発地：
                      {originText ? (
                        <span data-wovn-ignore>{originText}</span>
                      ) : (
                        <span style={styles.emptyText}>なし</span>
                      )}
                    </p>
                  </div>
                  <div style={styles.contentRow}>
                    <p style={styles.contentText}>
                      目的地：
                      {destinationText ? (
                        <span data-wovn-ignore>{destinationText}</span>
                      ) : (
                        <span style={styles.emptyText}>なし</span>
                      )}
                    </p>
                  </div>
                </div>
                <div style={styles.contentFlexRight}>
                  {hasDownloadInfo && (
                    <div style={styles.downloadInfoContainer}>
                      <p style={styles.downloadText}>
                        ダウンロード回数：<span data-wovn-ignore>{receiptRecord.downloaded_count || 0}</span>
                      </p>
                      {receiptRecord.downloaded_at && (
                        <p style={styles.downloadText}>
                          前回のダウンロード：
                          <span data-wovn-ignore>{formatDate(receiptRecord.downloaded_at.toString())}</span>
                        </p>
                      )}
                    </div>
                  )}

                  <div style={styles.totalPriceContainer}>
                    {totalPrice ? (
                      <p style={styles.totalPrice}>
                        <span>合計：</span>
                        <span data-wovn-ignore>{formatPrice(totalPrice)}</span>
                        <span>円</span>
                      </p>
                    ) : (
                      <p style={styles.totalPrice}>合計：確認中</p>
                    )}
                    <div
                      style={{
                        ...styles.statusContainer,
                        visibility: statusText ? 'visible' : 'hidden'
                      }}
                    >
                      {statusText && (
                        <>
                          <span style={styles.statusLabel}>{statusText}</span>
                          {statusText === '発行待ち' && (
                            <CustomTooltip
                              title={
                                <a
                                  href="https://aitravel.jp/knowledges/000001311"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: 'white', textDecoration: 'none' }}
                                >
                                  詳細は<span style={{ textDecoration: 'underline' }}>こちら</span>
                                </a>
                              }
                              placement="top"
                              interactive
                              leaveDelay={200}
                            >
                              <InfoOutlinedIcon style={styles.infoIcon} fontSize="small" />
                            </CustomTooltip>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Link style={styles.cardFlexRight} to={`/trips/${receiptRecord.id}?from=receipt`}>
              <ArrowForwardIcon style={styles.cardFlexRightIcon} />
            </Link>
          </div>
        </div>
      </div>
    );
  }
);

function getStyles(
  receiptRevisionStatus: 'none' | 'revising' | 'revised' | '',
  theme: Themes & AITTheme,
  isMobile: boolean
) {
  return {
    simpleCardWrap: {
      marginBottom: '25px',
      borderRadius: '5px'
    } as React.CSSProperties,

    simpleCard: {
      background: theme.fieldBgColor,
      borderRadius: '5px'
    } as React.CSSProperties,

    cardFlex: {
      display: 'flex',
      overflow: 'hidden',
      borderRadius: '5px'
    } as React.CSSProperties,

    cardFlexLeftCheck: {
      flex: '0 0 50px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    } as React.CSSProperties,

    cardFlexLeft: {
      flex: 1,
      padding: '16px',
      display: 'flex',
      flexDirection: 'column'
    } as React.CSSProperties,

    cardFlexRight: {
      flex: '0 0 50px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      backgroundColor: theme.grayBgColorLight,
      transition: 'background-color 0.3s',
      textDecoration: 'none'
    } as React.CSSProperties,

    cardFlexRightIcon: {
      color: theme.grayTextColor,
      border: `solid 2px ${lighten(theme.grayTextColor, 0.3)}`,
      borderRadius: '50%',
      padding: '2px',
      transition: 'color 0.3s, border-color 0.3s'
    } as React.CSSProperties,

    contentFlex: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      ...(isMobile && {
        flexDirection: 'column',
        flexWrap: 'wrap',
        gap: '16px',
        minHeight: 'auto',
        width: '100%',
        alignItems: 'flex-start'
      })
    } as React.CSSProperties,

    contentFlexLeft: {
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
      gap: '5px',
      flex: 1,
      ...(isMobile && {
        width: '100%'
      })
    } as React.CSSProperties,

    contentFlexRight: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '8px',
      ...(isMobile && {
        alignItems: 'flex-start'
      }),
      ...(!isMobile && {
        minWidth: '200px'
      })
    } as React.CSSProperties,

    contentRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    } as React.CSSProperties,

    contentTitle: {
      fontSize: '14px',
      fontWeight: 'bold',
      marginBottom: 0
    } as React.CSSProperties,

    contentIcons: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      listStyle: 'none'
    } as React.CSSProperties,

    contentId: {
      fontSize: '12px',
      margin: 0,
      color: theme.grayTextColor
    } as React.CSSProperties,

    contentText: {
      fontSize: '14px',
      margin: 0,
      color: theme.grayTextColor
    } as React.CSSProperties,

    emptyText: {
      color: theme.grayTextColor
    } as React.CSSProperties,

    totalPriceContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '20px',
      ...(isMobile && {
        width: '100%'
      }),
      ...(!isMobile && {
        minWidth: '200px'
      })
    } as React.CSSProperties,

    totalPrice: {
      fontWeight: 'bold',
      fontSize: '14px',
      margin: 0,
      whiteSpace: 'nowrap'
    } as React.CSSProperties,

    downloadInfoContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      width: '100%'
    } as React.CSSProperties,

    downloadText: {
      fontSize: '12px',
      margin: '2px 0',
      color: theme.grayTextColor,
      textAlign: 'left',
      width: '100%'
    } as React.CSSProperties,

    statusContainer: {
      margin: 0,
      padding: '8px 14px',
      background: theme.grayBgColor,
      borderRadius: '14px',
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      whiteSpace: 'nowrap'
    } as React.CSSProperties,

    statusLabel: {
      margin: 0,
      padding: 0,
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      // 青は旅程statusのArrangingの色に合わせた
      color: receiptRevisionStatus === 'revising' ? theme.redColor : '#0076bf'
    } as React.CSSProperties,

    infoIcon: {
      cursor: 'pointer',
      fontSize: '16px',
      color: theme.grayTextColor,
      verticalAlign: 'middle'
    } as React.CSSProperties
  };
}

export default TripReceiptRow;
